import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BookmarkPlus, ChevronDown, Trash2, Users, User } from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════════════════
   SavedFiltersMenu (Task #230 Phase C)

   Two controls at the right edge of CrmFilterBar:
     1. Preset dropdown — lists user's own + shared presets, click to load.
     2. Save button    — inline popover: name input + "Share with team" toggle.

   Props:
     resource     — 'contacts' | 'tasks' | 'opportunities'
     currentFilter — plain object matching CrmFilterBar state (to capture on Save)
     onLoad        — callback(filterJsonb) called when a preset is loaded
   ═══════════════════════════════════════════════════════════════════════════ */

export default function SavedFiltersMenu({ resource, currentFilter, onLoad }) {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showList, setShowList] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [isShared, setIsShared] = useState(false);
  const [saving, setSaving] = useState(false);

  const listRef = useRef(null);
  const saveRef = useRef(null);

  const loadPresets = useCallback(async () => {
    if (!resource) return;
    setLoading(true);
    try {
      const rows = await window.zignagenixAPI.getCRMFilterPresets({ resource });
      setPresets(Array.isArray(rows) ? rows : []);
    } catch {
      setPresets([]);
    } finally {
      setLoading(false);
    }
  }, [resource]);

  useEffect(() => { loadPresets(); }, [loadPresets]);

  // Close popovers on outside click
  useEffect(() => {
    if (!showList && !showSave) return;
    const handle = (e) => {
      if (listRef.current && !listRef.current.contains(e.target)) setShowList(false);
      if (saveRef.current && !saveRef.current.contains(e.target)) setShowSave(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [showList, showSave]);

  const handleSave = async () => {
    const name = saveName.trim();
    if (!name) return;
    setSaving(true);
    try {
      await window.zignagenixAPI.saveCRMFilterPreset({
        name,
        resource,
        filterJsonb: currentFilter || {},
        isShared,
      });
      setSaveName('');
      setIsShared(false);
      setShowSave(false);
      loadPresets();
    } catch (e) {
      alert('Could not save filter: ' + (e?.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this saved filter?')) return;
    try {
      await window.zignagenixAPI.deleteCRMFilterPreset(id);
      setPresets(p => p.filter(x => x.id !== id));
    } catch (err) {
      alert('Delete failed: ' + (err?.message || 'Unknown error'));
    }
  };

  return (
    <div className="crm-presets" aria-label="Saved filter presets">

      {/* ─── Preset list dropdown ─── */}
      <div ref={listRef} style={{ position: 'relative' }}>
        <button
          type="button"
          className="crm-presets__toggle"
          onClick={() => { setShowList(v => !v); setShowSave(false); }}
          title="Saved filters"
          aria-label="Open saved filters"
          aria-haspopup="listbox"
          aria-expanded={showList}
        >
          <ChevronDown size={12} aria-hidden="true" />
          {presets.length > 0 && (
            <span className="crm-presets__badge" aria-label={`${presets.length} saved filters`}>
              {presets.length}
            </span>
          )}
        </button>

        {showList && (
          <div className="crm-presets__dropdown" role="listbox" aria-label="Saved filter presets">
            {loading && (
              <div className="crm-presets__empty">Loading…</div>
            )}
            {!loading && presets.length === 0 && (
              <div className="crm-presets__empty">No saved filters yet</div>
            )}
            {!loading && presets.map(p => (
              <div
                key={p.id}
                className="crm-presets__item"
                role="option"
                tabIndex={0}
                aria-label={p.name + (p.is_shared ? ' (shared)' : '')}
                onClick={() => { onLoad(p.filter_jsonb); setShowList(false); }}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onLoad(p.filter_jsonb);
                    setShowList(false);
                  }
                }}
              >
                <span className="crm-presets__item-icon" aria-hidden="true">
                  {p.is_shared ? <Users size={11} /> : <User size={11} />}
                </span>
                <span className="crm-presets__item-name">{p.name}</span>
                {p.creator_name && p.is_shared && (
                  <span className="crm-presets__item-creator">{p.creator_name}</span>
                )}
                <button
                  type="button"
                  className="crm-presets__item-delete"
                  onClick={(e) => handleDelete(e, p.id)}
                  aria-label={`Delete preset "${p.name}"`}
                  tabIndex={-1}
                >
                  <Trash2 size={10} aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ─── Save popover ─── */}
      <div ref={saveRef} style={{ position: 'relative' }}>
        <button
          type="button"
          className="crm-presets__save-btn"
          onClick={() => { setShowSave(v => !v); setShowList(false); }}
          title="Save current filter as preset"
          aria-label="Save current filter"
          aria-haspopup="dialog"
          aria-expanded={showSave}
        >
          <BookmarkPlus size={13} aria-hidden="true" />
        </button>

        {showSave && (
          <div className="crm-presets__popover" role="dialog" aria-label="Save filter preset">
            <input
              type="text"
              placeholder="Filter name…"
              value={saveName}
              maxLength={64}
              onChange={e => setSaveName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') handleSave();
                if (e.key === 'Escape') setShowSave(false);
              }}
              autoFocus
              className="crm-presets__name-input"
              aria-label="Preset name"
            />
            <label className="crm-presets__share-label">
              <input
                type="checkbox"
                checked={isShared}
                onChange={e => setIsShared(e.target.checked)}
                aria-label="Share with team"
              />
              Share with team
            </label>
            <div className="crm-presets__popover-actions">
              <button
                type="button"
                className="crm-presets__confirm"
                onClick={handleSave}
                disabled={saving || !saveName.trim()}
                aria-label="Confirm save"
              >
                {saving ? '…' : 'Save'}
              </button>
              <button
                type="button"
                className="crm-presets__cancel"
                onClick={() => setShowSave(false)}
                aria-label="Cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
